import styled from '@emotion/styled';
import { Button, Card, Dropdown, Flex, Table, type TableProps, theme, Typography } from 'antd';
import { type ItemType, type MenuItemGroupType, type MenuItemType } from 'antd/es/menu/interface';
import { type ColumnType } from 'antd/es/table';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  ChevronsUpDown,
  Ellipsis,
  SquareMinus,
  SquarePlus,
} from 'lucide-react';
import React, {
  forwardRef,
  type ReactElement,
  type ReactNode,
  type Ref,
  useImperativeHandle,
  useMemo,
} from 'react';
import { type BaseEntity, type IResult } from '../jsonapi-provider';

const StyledTable = styled(Table)`
  .ant-table {
    background-color: transparent;
  }
  .ant-table-thead .ant-table-cell:first-of-type {
    padding-inline-start: var(--ant-padding-lg) !important;
  }

  .ant-table-tbody .ant-table-cell:first-of-type {
    padding-inline-start: var(--ant-padding-lg) !important;
  }

  .ant-table-thead .ant-table-cell {
    padding-inline-end: var(--ant-padding-lg) !important;
    text-transform: uppercase;
    font-size: 0.9em;
    font-weight: 600;
    opacity: 0.85;
  }
` as unknown as typeof Table;

// 1) Your “own” props
export interface DataTableOwnProps<T, P extends T = T> {
  block?: boolean;
  currentPage?: number;
  title: string;
  description: string;
  filtersNode?: ReactNode;
  actionsNode?: ReactNode;
  dataList: IResult<T[]> | T[];
  totalRecords?: number;
  dataListTransformer?: (data: T[]) => P[];
  onChange: (change: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload: Record<string, any>;
  }) => void;

  // these overlap with TableProps<P>
  columns: Exclude<TableProps<P>['columns'], undefined>;
  expandable?: TableProps<P>['expandable'];
  // rowSelection is part of TableProps<P>, but you only want its `onChange`
  onSelectionChange?: Exclude<TableProps<P>['rowSelection'], undefined>['onChange'];

  // your additional custom bits
  actions?: Array<
    ItemType<
      MenuItemType & {
        onItemClick?: (record: P, index: number) => void;
        isDisabled?: (record: P, index: number) => boolean;
        isHidden?: (record: P, index: number) => boolean;
      }
    >
  >;
  selectMode?: 'single' | 'multiple' | 'none';
  selectedRowKeys?: string[];
  pageSize?: number;
  bulkActionsNode?: ReactNode;
}

// 2) Export a merged type that Omit<…>s out any keys you’ve already declared
export type DataTableProps<T, P extends T = T> = DataTableOwnProps<T, P> &
  Omit<TableProps<P>, keyof DataTableOwnProps<T, P>>;

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE_NUMBER = 1;

const DataTableRenderFn = function <T extends BaseEntity, P extends T = T>(
  props: DataTableProps<T, P>,
  ref: Ref<any>
): ReactNode {
  const {
    block = false,
    currentPage = undefined,
    dataListTransformer = (data) => data as P[],
    dataList,
    onChange,
    title,
    columns,
    description,
    actionsNode,
    filtersNode,
    selectMode = 'none',
    selectedRowKeys = [],
    expandable,
    actions,
    pageSize = DEFAULT_PAGE_SIZE,
    bulkActionsNode,
    onSelectionChange,
    loading = false,
    totalRecords,
    ...remainingProps
  } = props;

  const { token } = theme.useToken();
  const transformedData = useMemo(
    () => dataListTransformer(Array.isArray(dataList) ? dataList : dataList.data ?? []),
    [Array.isArray(dataList) ? dataList : dataList.data]
  );

  const transformedColumns: DataTableProps<P>['columns'] = useMemo(
    () =>
      // (selectMode === 'single' || selectMode === 'multiple' ? [Table.SELECTION_COLUMN] : [])
      //   .concat(expandable ? [Table.EXPAND_COLUMN] : [])
      ([] as DataTableProps<P>['columns']).concat(
        columns.map((col, index) =>
          Object.assign({}, col, {
            sortIcon: ({ sortOrder }: { sortOrder: ColumnType<any>['sortOrder'] }) =>
              !sortOrder ? (
                <ChevronsUpDown style={{ color: token.colorTextDisabled }} />
              ) : sortOrder === 'ascend' ? (
                <ChevronsDown style={{ color: token.colorPrimary }} />
              ) : (
                <ChevronsUp style={{ color: token.colorPrimary }} />
              ),
          })
        ),
        actions
          ? [
              {
                width: 83,
                align: 'right',
                title: 'Actions',
                key: 'action',
                fixed: 'right',
                render: (record) => (
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      style: { minWidth: 120 },
                      items: actions
                        // @ts-expect-error complex type assertion required
                        ?.filter((item) => !item?.isHidden?.(record))
                        .map((item, idx) =>
                          Object.assign({}, item, {
                            onClick: () => {
                              // @ts-expect-error complex type assertion required
                              item?.onItemClick?.(record, idx);
                            },
                            // @ts-expect-error complex type assertion required
                            disabled: item?.isDisabled?.(record, idx),
                            children: (item as MenuItemGroupType)?.children
                              // @ts-expect-error complex type assertion required
                              ?.filter((item) => !item?.isHidden?.(record, idx))
                              .map((child, idx) =>
                                Object.assign({}, child, {
                                  onClick: () => {
                                    // @ts-expect-error complex type assertion required
                                    child?.onItemClick?.(record, idx);
                                  },
                                  // @ts-expect-error complex type assertion required
                                  disabled: child?.isDisabled?.(record, idx),
                                  onItemClick: undefined,
                                  isDisabled: undefined,
                                  isHidden: undefined,
                                })
                              ),
                            onItemClick: undefined,
                            isDisabled: undefined,
                            isHidden: undefined,
                          })
                        ),
                    }}
                  >
                    <Button type="text">
                      <Ellipsis />
                    </Button>
                  </Dropdown>
                ),
              },
            ]
          : []
      ),
    [columns]
  );

  const rowSelection: TableProps<P>['rowSelection'] = useMemo(
    () =>
      selectMode === 'multiple'
        ? {
            hideSelectAll: false,
            columnWidth: 0,
            // defaultSelectedRowKeys: selectedRowKeys,
            selectedRowKeys,
            onChange: onSelectionChange,
          }
        : selectMode === 'single'
        ? {
            hideSelectAll: true,
            columnWidth: 0,
            renderCell: () => '',
            // defaultSelectedRowKeys: selectedRowKeys,
            selectedRowKeys,
            onChange: onSelectionChange,
          }
        : undefined,
    [selectMode, selectedRowKeys]
  );

  useImperativeHandle(ref, () => ({}), []);

  return (
    <Flex gap="small" vertical>
      <Flex justify="space-between" gap="middle" wrap="wrap">
        {filtersNode && (
          <Flex flex="1 1" justify="flex-start">
            {filtersNode}
          </Flex>
        )}
        {actionsNode && (
          <Flex flex="1 0" justify="flex-end">
            {actionsNode}
          </Flex>
        )}
      </Flex>
      <Card
        style={
          block
            ? {
                marginInline: -token.marginLG,
                borderRadius: 0,
              }
            : undefined
        }
        styles={{ body: { padding: 0 } }}
      >
        <StyledTable<P>
          className="data-table"
          title={() => (
            <Flex
              gap="middle"
              style={{ paddingInline: token.paddingMD, paddingBlockStart: token.paddingSM }}
            >
              <Flex vertical>
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {title}
                </Typography.Title>
                <Typography.Text
                  style={{ marginBlockStart: token.marginXS, cursor: 'initial' }}
                  disabled
                >
                  {description}
                </Typography.Text>
              </Flex>
              {bulkActionsNode ? <Flex>{bulkActionsNode}</Flex> : null}
            </Flex>
          )}
          size="middle"
          rowKey={(row: P) => row.id!}
          expandable={
            expandable
              ? Object.assign({}, expandable, {
                  expandIcon: ({ expanded, onExpand, expandable, record }) =>
                    expandable && expanded ? (
                      <SquareMinus
                        style={{ marginBlockStart: token.marginXXS, cursor: 'pointer' }}
                        onClick={(e) => {
                          onExpand(record, e as unknown as React.MouseEvent<HTMLElement>);
                        }}
                      />
                    ) : expandable && !expanded ? (
                      <SquarePlus
                        style={{ marginBlockStart: token.marginXXS, cursor: 'pointer' }}
                        onClick={(e) => {
                          onExpand(record, e as unknown as React.MouseEvent<HTMLElement>);
                        }}
                      />
                    ) : (
                      ''
                    ),
                } satisfies Partial<TableProps<P>['expandable']>)
              : expandable
          }
          rowSelection={rowSelection}
          loading={Array.isArray(dataList) ? loading : dataList.isLoading || loading}
          dataSource={transformedData}
          onChange={(_p, _f, sorter) => {
            onChange({
              type: 'SET_SORT',
              payload: Array.isArray(sorter) ? sorter[0] : sorter,
            });
          }}
          pagination={{
            hideOnSinglePage: true,
            position: ['bottomCenter'],
            showSizeChanger: false,
            defaultPageSize: pageSize,
            defaultCurrent: DEFAULT_PAGE_NUMBER,
            ...(currentPage ? { current: currentPage } : {}),
            total:
              totalRecords !== undefined
                ? totalRecords
                : Array.isArray(dataList)
                ? dataList.length
                : dataList.meta?.page.totalRecords,
            onChange: (current, pageSize) => {
              onChange({ type: 'SET_PAGINATION', payload: { current, pageSize } });
            },
            itemRender: (_page, type, org) => {
              if (type === 'prev')
                return (
                  <Button
                    type="link"
                    size="large"
                    className="ant-pagination-item-link"
                    icon={<ChevronLeft />}
                    iconPosition="start"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      padding: token.paddingSM,
                    }}
                  >
                    Previous
                  </Button>
                );
              if (type === 'next')
                return (
                  <Button
                    type="link"
                    size="large"
                    className="ant-pagination-item-link"
                    icon={<ChevronRight />}
                    iconPosition="end"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      padding: token.paddingSM,
                    }}
                  >
                    Next
                  </Button>
                );
              return org;
            },
          }}
          columns={transformedColumns}
          scroll={{ x: true }}
          {...remainingProps}
        />
      </Card>
    </Flex>
  );
};

export const DataTable = forwardRef(DataTableRenderFn) as <T, P extends T = T>(
  props: DataTableProps<T, P> & { ref?: Ref<any> }
) => ReactElement;

// DataTableRenderFn.defaultProps = {
//   filtersNode: null,
//   addButtonNode: null,
//   dataListTransformer: (data: T[]) => data,
//   selectMode: 'none',
// };
//
// DataTableRenderFn.displayName = 'DataTable';
